import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Col, Input, Button } from 'reactstrap';
import UUID from 'uuid';
import { publish, listen } from '../../services/communicator';
import JSEncrypt from 'jsencrypt';

const PUBLIC_KEY = `
-----BEGIN PUBLIC KEY-----
MFwwDQYJKoZIhvcNAQEBBQADSwAwSAJBAKarqy3E05jS0GW
KROubY92VZHHig0WmyZIpDJQq1/4q61E3LUiMo4Hha1BBxV
NlzdJjfq/HeHbXpWo4BUG+w58CAwEAAQ==
-----END PUBLIC KEY-----
`.trim();
const PRIVATE_KEY = `
-----BEGIN RSA PRIVATE KEY-----
MIIBVAIBADANBgkqhkiG9w0BAQEFAASCAT4wggE6AgEAAkE
AgT4tgFeFL9qdDFIlR5uLyUXkD60qZpwPgjS6GqEMaNVjNa
bi/v/x6MaxAkvZwJsWWUV1Hi3t1MZ7cBkjBvctoQIDAQABA
kBX2/UCsnyQM4Vfwtgk/dDhWunW3llct+qzETUStTjCapN/
7in+vjLktyguJTFTdpCcYhXSBp5YCgEdgsw22VIhAiEA09L
bA33JNz5ui0HUX3awdxvB6Cwd1NU7bCHUCNAsoJ8CIQCcMm
G4W20lkm3FR2ZtI02pSl6f/7xusRKRrQj3IWJJvwIhAINdz
T4JEHUWXvA2T0M1FJOJv2QBmC6aX1PcVudwU3z5AiArgCyO
94dyS1Ge+7+e7ZIXb8rirIqCGXaec55nlTiWaQIgTGwNJSX
TQuAyA6BzYtkkH2LK4qsaMtjs23zO7sjHK0I=
-----END RSA PRIVATE KEY-----
`.trim();

class InspectModal extends React.Component {

  state = {
    seed: '',
    inspecting: false
  };

  isValid() {
    return (
      /^[0-9A-F]{1,4}$/i.test(this.state.seed)
    );
  }

  onFieldChange = (event) => {
    return this.setState({
      [event.target.name]: event.target.value
    })
  };

  getField(name, valueProp = 'value', defaultValue = '') {
    const from = this.state;
    return {
      name,
      id: name,
      [valueProp]: from[name] || defaultValue,
      onChange: this.onFieldChange,
      disabled: this.props.disabled || this.state.inspecting
    }
  }

  onToggle = () => {
    if (event && event.preventDefault && this.props.onToggle) {
      event.preventDefault();
    }
    this.props.onToggle();
  };

  onInspectCaught = async () => {
    const uuid = UUID.v4();
    const token = InspectModal.randomUint32();
    const request = InspectModal.getCRCRequest(uuid, token, this.state.seed);
    // Start listening before we send our message
    let resolve, reject;
    const responsePromise = new Promise(
      (resolveFn, rejectFn) => {
        resolve = resolveFn;
        reject = rejectFn;
      }
    );
    const cancel = await listen(
      'crc-response',
      (payload) => {
        const response = InspectModal.getCRCResponse(payload);
        if (response.uuid !== uuid) {
          return;
        }
        cancel();
        resolve(response);
      },
      (error) => {
        cancel();
        reject(error);
      }
    );
    await publish(`crc-request:${this.props.productInstance.uuid}`, request);
    const response = await responsePromise;
    // Ensure the value received is read as unsined
    if (new Uint32Array([response.token])[0] !== token) {
      throw new Error('Invalid token');
    }
    await new Promise(resolve => this.setState({
      crc: response.crcHex || response.crc
    }, resolve));
  };

  onInspect = async () => {
    if (!this.isValid()) {
      return null;
    }
    await new Promise(resolve => this.setState({
      inspecting: true,
      error: false
    }, resolve));
    try {
      await this.onInspectCaught();
    } catch(e) {
      await new Promise(resolve => this.setState({
        error: true
      }, resolve));
    }
    await new Promise(resolve => this.setState({
      inspecting: false
    }, resolve));
  };

  static randomUint32() {
    if (window && window.crypto && window.crypto.getRandomValues && Uint32Array) {
      const bytes = new Uint32Array(1);
      window.crypto.getRandomValues(bytes);
      return bytes[0];
    } else {
      return Math.floor(Math.random() * Math.pow(2, 32));
    }
  }

  static getTokenAndSeedString(token, seed) {
    const seedInt = parseInt(seed, 16);
    // This json string MUST be over 6 bytes, the keys are enough to ensure this is the case
    const json = JSON.stringify({
      seed: seedInt,
      seedHex: seed,
      token
    });
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(PUBLIC_KEY);
    return encrypt.encrypt(json);
  }

  static getCRCRequest(uuid, token, seed) {
    const tokenAndSeed = InspectModal.getTokenAndSeedString(token, seed);
    return JSON.stringify({
      uuid,
      tokenAndSeed
    });
  }

  static getCRCAndTokenFromString(encrypted) {
    const encrypt = new JSEncrypt();
    encrypt.setPrivateKey(PRIVATE_KEY);
    const decrypted = encrypt.decrypt(encrypted);
    const response = JSON.parse(decrypted);
    return {
      ...response,
      // Ensure we use an unsigned 32bit int instead of a floating point
      token: new Uint32Array([response.token])[0]
    }
  }

  static getCRCResponse(json) {
    const { uuid, crcAndToken } = JSON.parse(json);
    if (!(uuid && crcAndToken)) {
      return {};
    }
    const { token, crc, crcHex } = InspectModal.getCRCAndTokenFromString(crcAndToken);
    return {
      uuid,
      token,
      crc,
      crcHex
    };
  }

  render() {
    return (
      <React.Fragment>
        <Modal isOpen={this.props.isOpen} toggle={this.onToggle} size="lg">
          <Form onSubmit={this.onSubmit}>
            <ModalHeader toggle={this.onToggle}>Inspect Site Controller via CRC</ModalHeader>
            <ModalBody>
              <FormGroup row>
                <Label for="seed" sm={3}>Seed</Label>
                <Col sm={9}>
                  <Input {...this.getField('seed')} type="text" placeholder="Seed (4 hex)" max={4} />
                </Col>
              </FormGroup>
              {
                this.state.crc ? (
                  <p>Calculated CRC: {typeof this.state.crc === 'number' ? this.state.crc.toString(16) : this.state.crc}</p>
                ) : null
              }
            </ModalBody>
            <ModalFooter className="d-flex flex-row justify-content-between align-items-center">
              <div>
                <Button color="primary" disabled={this.state.inspecting || !this.isValid()} onClick={this.onInspect}>Compute CRC with provided seed</Button>
                {' '}
                <Button color="secondary" onClick={this.onToggle}>Cancel</Button>
              </div>
            </ModalFooter>
          </Form>
        </Modal>
      </React.Fragment>
    )
  }

}

export default InspectModal;


// WEBPACK FOOTER //
// ./src/app/inspector/inspect-modal.js